import React, { useEffect, useMemo } from "react";
import { Typography, Space, Button, App, Alert, List, Skeleton, theme } from "antd";
import { useLanguage } from "../providers/LanguageProvider";
import useSessions from "src/hooks/useSessions";
import type { ISession } from "src/hooks/useSessions";
import SessionItem from "src/components/sessions/SessionItem";
import useChangePasswordHistory from "src/hooks/useChangePasswordHistory";

const { Title, Text } = Typography;

function getCurrentClientId() {
  return localStorage.getItem("clientId");
}
function safeDate(iso?: string | null) {
  const dt = iso ? new Date(iso) : null;
  return dt && !Number.isNaN(dt.getTime()) ? dt : null;
}

export default function SessionsPage() {
  const { message } = App.useApp();
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const session = useSessions() as any;
  const sessions: ISession[] = session?.sessions ?? session?.data ?? [];
  const loading: boolean = session?.loading ?? session?.isLoading ?? false;
  const error: string | null = session?.error ?? null;

  const changePasswordHistory = useChangePasswordHistory();

  const fetchSessions: (() => Promise<any>) | undefined =
    session?.fetchSessions ?? session?.reload;
  const logoutSession: ((id: string | number) => Promise<any>) | undefined =
    session?.logoutSession ?? session?.revokeSession;
  // const logoutAll: (() => Promise<any>) | undefined =
  //   session?.logoutAll ?? session?.revokeAll;

  useEffect(() => {
    fetchSessions?.();
    changePasswordHistory?.fetchData?.();
  }, []);

  const currentClientId = getCurrentClientId();
  const getLastSeen = (s: ISession) => s.LastActivityAt || s.UpdatedAt || s.CreatedAt || null;

  const sorted = useMemo(() => {
    const list = [...(sessions ?? [])];
    list.sort((a, b) => {
      const aT = safeDate(getLastSeen(a))?.getTime() ?? 0;
      const bT = safeDate(getLastSeen(b))?.getTime() ?? 0;
      return bT - aT;
    });
    return list;
  }, [sessions]);

  const handleLogoutSession = async (id: string | number) => {
    try {
      if (logoutSession) await logoutSession(id);
      session?.setSessions?.((prev: ISession[]) => prev.filter((s) => s.Id !== id));
      message.success(t("SESSION_LOGGED_OUT"));
    } catch (e: any) {
      message.error(e?.message || t("ERROR_GENERIC"));
    }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          {t("MENU_SESSIONS")}
        </Title>
        <Space>
          {/* <Button danger onClick={logoutAll} disabled={!logoutAll}>
            {t("LOGOUT_ALL")}
          </Button> */}
          <Button onClick={() => fetchSessions?.()} disabled={!fetchSessions}>
            {t("BTN_REFRESH")}
          </Button>
        </Space>
      </div>

      {error && (
        <Alert type="error" showIcon message={t("ERROR")} description={String(error)} />
      )}

      {loading && (
        <List
          itemLayout="vertical"
          dataSource={Array.from({ length: 5 }).map((_, i) => i)}
          renderItem={(i) => (
            <List.Item
              key={i}
              style={{ padding: 0, borderBlockEnd: `1px solid ${token.colorSplit}` }}
            >
              <div style={{ paddingBlock: 8 }}>
                <Skeleton active avatar paragraph={{ rows: 2 }} />
              </div>
            </List.Item>
          )}
        />
      )}

      {!loading && sorted.length > 0 && (
        <List
          itemLayout="vertical"
          dataSource={sorted}
          split
          renderItem={(s: ISession) => {
            const isCurrent = !!currentClientId && s.ClientId === currentClientId;
            return (
              <List.Item
                key={String(s.Id)}
                style={{ padding: 0, borderBlockEnd: `1px solid ${token.colorSplit}` }}
              >
                <SessionItem session={s} isCurrent={isCurrent} onLogout={handleLogoutSession} />
              </List.Item>
            );
          }}
        />
      )}

      {!loading && sorted.length === 0 && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Text type="secondary">{t("SESSIONS_EMPTY")}</Text>
        </div>
      )}
    </div>
  );
}
